import { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Users, Download, Filter, LineChart } from "lucide-react";
import { toast } from "sonner";

const SEG_COLORS = {
  "New": "bg-blue-100 text-blue-700",
  "Repeat": "bg-green-100 text-green-700",
  "VIP": "bg-purple-100 text-purple-700",
  "High-Value": "bg-amber-100 text-amber-800",
  "Dormant": "bg-gray-200 text-gray-700",
  "Reactivated": "bg-teal-100 text-teal-700",
  "Discount-Driven": "bg-pink-100 text-pink-700",
  "Category-Specific": "bg-indigo-100 text-indigo-700",
};

const SEGMENTS = ["All", "New", "Repeat", "VIP", "High-Value", "Dormant", "Reactivated", "Discount-Driven", "Category-Specific"];

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return "—"; }
}

export default function AdminCustomerAnalytics() {
  const [data, setData] = useState({ summary: { total_customers: 0, by_segment: {}, by_source: {}, top_acquisition_source: null }, customers: [] });
  const [cohorts, setCohorts] = useState([]);
  const [query, setQuery] = useState("");
  const [seg, setSeg] = useState("All");
  const [src, setSrc] = useState("All");

  useEffect(() => {
    api.get("/admin/customers/analytics").then((r) => setData(r.data)).catch(() => toast.error("Could not load analytics"));
    api.get("/admin/analytics/cohort-retention").then((r) => setCohorts(r.data.cohorts || [])).catch(() => setCohorts([]));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.customers.filter((c) => {
      if (seg !== "All" && c.segment !== seg) return false;
      if (src !== "All" && c.acquisition_source !== src) return false;
      if (!q) return true;
      return [c.full_name, c.email, c.phone, c.city, c.state, c.pincode, c.customer_code]
        .some((v) => v && String(v).toLowerCase().includes(q));
    });
  }, [data.customers, query, seg, src]);

  const exportCsv = () => {
    if (!filtered.length) return toast.error("No rows to export");
    const headers = ["customer_code","full_name","phone","email","city","state","pincode","registration_date","acquisition_source","first_purchase_date","last_purchase_date","total_orders","total_spending","avg_order_value","unique_vendors","unique_categories","total_refunds","total_returns","cancellations","segment"];
    const rows = filtered.map((c) => headers.map((h) => `"${(c[h] ?? "").toString().replace(/"/g, '""')}"`).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `cityshor_customers_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const sources = useMemo(() => ["All", ...Object.keys(data.summary.by_source || {}).sort()], [data.summary]);

  return (
    <div className="space-y-6" data-testid="customer-analytics">
      <div className="grid gap-3 md:grid-cols-3">
        <div className="p-5 rounded-2xl bg-white border border-gray-200" data-testid="analytics-total">
          <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1">Total customers</div>
          <div className="font-display text-3xl flex items-center gap-2"><Users className="h-6 w-6 text-[#F48E2E]" /> {data.summary.total_customers}</div>
        </div>
        <div className="p-5 rounded-2xl bg-white border border-gray-200 md:col-span-1">
          <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">By segment</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(data.summary.by_segment || {}).map(([k, v]) => (
              <span key={k} data-testid={`seg-count-${k}`} className={`text-[11px] px-2 py-1 rounded-full font-medium ${SEG_COLORS[k] || "bg-gray-100 text-gray-700"}`}>
                {k} · {v}
              </span>
            ))}
            {!Object.keys(data.summary.by_segment || {}).length && <span className="text-xs text-gray-500">No customers yet.</span>}
          </div>
        </div>
        <div className="p-5 rounded-2xl bg-white border border-gray-200" data-testid="top-source">
          <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1">Top acquisition source</div>
          <div className="font-display text-2xl">{data.summary.top_acquisition_source || "—"}</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Object.entries(data.summary.by_source || {}).map(([k, v]) => (
              <span key={k} className="text-[11px] px-2 py-1 rounded-full bg-gray-100 text-gray-700">{k} · {v}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="p-5 rounded-2xl bg-white border border-gray-200" data-testid="cohort-retention-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Cohort retention</div>
            <div className="font-display text-lg flex items-center gap-2"><LineChart className="h-4 w-4 text-[#F48E2E]" /> % of each week's buyers who bought again</div>
          </div>
          <div className="text-[10px] text-gray-400">Last 12 weeks · reads left→right</div>
        </div>
        {cohorts.length === 0 ? (
          <div className="text-xs text-gray-500">Not enough sign-ups yet to plot a retention heatmap.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="cohort-table">
              <thead className="text-left text-gray-500">
                <tr>
                  <th className="py-1 pr-3">Cohort week</th>
                  <th className="py-1 pr-3 text-right">Signups</th>
                  <th className="py-1 pr-3 text-center">Month 2</th>
                  <th className="py-1 pr-3 text-center">Month 3</th>
                  <th className="py-1 pr-3 text-center">Month 6</th>
                </tr>
              </thead>
              <tbody>
                {cohorts.map((c) => {
                  const cell = (label, pct) => {
                    const p = Math.max(0, Math.min(100, pct));
                    const bg = p === 0 ? "#F3F4F6" : `hsl(148, 60%, ${Math.max(30, 95 - p * 0.5)}%)`;
                    const fg = p > 40 ? "#fff" : "#111827";
                    return (
                      <td key={label} className="py-1 pr-3 text-center">
                        <span className="inline-block min-w-[52px] px-2 py-1 rounded-md font-mono-tab" style={{ backgroundColor: bg, color: fg }}>{p.toFixed(1)}%</span>
                      </td>
                    );
                  };
                  return (
                    <tr key={c.cohort} className="border-t border-gray-100" data-testid={`cohort-row-${c.cohort}`}>
                      <td className="py-1 pr-3 font-mono-tab">{c.cohort}</td>
                      <td className="py-1 pr-3 text-right font-mono-tab">{c.size}</td>
                      {cell("m2", c.m2_pct)}
                      {cell("m3", c.m3_pct)}
                      {cell("m6", c.m6_pct)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Filter className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input data-testid="analytics-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, phone, city, pincode, customer code" className="pl-9 rounded-full" />
        </div>
        <select data-testid="analytics-seg-filter" value={seg} onChange={(e) => setSeg(e.target.value)} className="h-9 rounded-full border border-gray-300 px-3 text-sm bg-white">
          {SEGMENTS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="analytics-src-filter" value={src} onChange={(e) => setSrc(e.target.value)} className="h-9 rounded-full border border-gray-300 px-3 text-sm bg-white">
          {sources.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <Button data-testid="analytics-export-csv" onClick={exportCsv} variant="outline" className="rounded-full"><Download className="h-4 w-4 mr-2" />Export CSV</Button>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="p-3 whitespace-nowrap">Customer ID</th>
              <th className="p-3">Name</th>
              <th className="p-3">Contact</th>
              <th className="p-3">City / State</th>
              <th className="p-3">Registered</th>
              <th className="p-3">Source</th>
              <th className="p-3">First → last order</th>
              <th className="p-3 text-right">Orders</th>
              <th className="p-3 text-right">Spend</th>
              <th className="p-3 text-right">AOV</th>
              <th className="p-3 text-right">Vendors</th>
              <th className="p-3 text-right">Cats</th>
              <th className="p-3 text-right">Ret / Ref</th>
              <th className="p-3 text-right">Cancels</th>
              <th className="p-3">Segment</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-gray-100" data-testid={`analytics-row-${c.id}`}>
                <td className="p-3 font-mono-tab text-xs">{c.customer_code}</td>
                <td className="p-3">{c.full_name || "—"}</td>
                <td className="p-3 text-xs">
                  <div>{c.email}</div>
                  {c.phone && <div className="text-gray-500">{c.phone}</div>}
                </td>
                <td className="p-3 text-xs">{c.city ? `${c.city}${c.state ? ", " + c.state : ""}` : "—"}{c.pincode ? ` · ${c.pincode}` : ""}</td>
                <td className="p-3 text-xs">{fmt(c.registration_date)}</td>
                <td className="p-3 text-xs">{c.acquisition_source}</td>
                <td className="p-3 text-xs">{fmt(c.first_purchase_date)} → {fmt(c.last_purchase_date)}</td>
                <td className="p-3 text-right font-mono-tab">{c.total_orders}</td>
                <td className="p-3 text-right font-mono-tab">₹{Number(c.total_spending).toLocaleString("en-IN")}</td>
                <td className="p-3 text-right font-mono-tab">₹{Number(c.avg_order_value).toLocaleString("en-IN")}</td>
                <td className="p-3 text-right font-mono-tab">{c.unique_vendors}</td>
                <td className="p-3 text-right font-mono-tab">{c.unique_categories}</td>
                <td className="p-3 text-right font-mono-tab text-xs">{c.total_returns} / {c.total_refunds}</td>
                <td className="p-3 text-right font-mono-tab">{c.cancellations}</td>
                <td className="p-3">
                  <span className={`text-[11px] px-2 py-1 rounded-full font-medium ${SEG_COLORS[c.segment] || "bg-gray-100 text-gray-700"}`}>{c.segment}</span>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={15} className="p-8 text-center text-gray-500 text-sm">No customers match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
